import React, { useState, useCallback, useRef, useEffect, useContext, useMemo } from 'react';
import { ThemeProvider } from 'styled-components';
import _, { debounce } from 'lodash';
import { AsyncAutocompleteProps } from './types';
import { ThemeContext } from '..';
import { InputContainer } from '../ToggleSwitch/styles';
import Select from '../Select/Select';
import { getPropertyByPathString } from '../utils/arrays';

const AsyncAutoComplete: React.FC<AsyncAutocompleteProps> = (props) => {
  const {
    options = [],
    input,
    placeholder,
    disabled = false,
    readOnly,
    label,
    keyProperty = 'id',
    showProperty = 'name',
    testId,
    isLoading,
    isValueObject,
    overAll = false,
    getOptions,
    onChange,
    onFocus,
    onBlur,
  } = props;

  const inputRef: React.Ref<HTMLInputElement> = useRef(null);
  const optionSelected = useMemo(
    () => options.find((op) => _.get(op, keyProperty) === input?.value),
    [input?.value, keyProperty, options],
  );
  const [value, setValue] = useState(
    isValueObject
      ? _.get(input?.value, showProperty)
      : optionSelected && _.get(optionSelected, showProperty),
  );
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState({
    index: 0,
    id: '',
  });

  const handleChange = useCallback(
    (newValue: string) => {
      setValue(newValue);
      if (newValue !== '' && newValue.length >= 3) {
        getOptions(newValue);
        setIsOpen(true);
      }
    },
    [getOptions],
  );

  const debouncedResults = useMemo(() => debounce(handleChange, 50), [handleChange]);

  useEffect(() => {
    debouncedResults.cancel();
  });

  const { actualTheme } = useContext(ThemeContext);

  const handleBlur = useCallback(() => {
    const selectedOption = options.find((option) =>
      isValueObject
        ? input.value === option
        : input.value === getPropertyByPathString(option, keyProperty),
    );
    if (selectedOption) {
      setValue(_.get(selectedOption, showProperty));
    } else {
      setValue('');
    }
    setIsOpen(false);
    input.onBlur();
    if (onBlur) onBlur();
  }, [input, isValueObject, keyProperty, onBlur, options, showProperty]);

  const onSelectOption = useCallback(
    (valueChange: any) => {
      if (input) input.onChange(isValueObject ? valueChange : _.get(valueChange, keyProperty));
      if (onChange) onChange(isValueObject ? valueChange : _.get(valueChange, keyProperty));
      setValue(_.get(valueChange, showProperty));
    },
    [input, isValueObject, keyProperty, onChange, showProperty],
  );

  const expand = useCallback(() => {
    inputRef.current.focus();
  }, []);

  const handleFocus = useCallback(() => {
    setIsOpen(false);
    if (onFocus) onFocus();
  }, [onFocus]);

  const handleClear = useCallback(() => {
    if (input) {
      input.onChange('');
    }
    if (onChange) onChange('');
    handleChange('');
  }, [handleChange, input, onChange]);

  return (
    <ThemeProvider theme={actualTheme}>
      <InputContainer id="async-autocomplete-id">
        <Select
          isLoading={isLoading}
          label={label}
          placeholder={placeholder}
          disabled={disabled}
          {...props}
          inputRef={inputRef}
          readOnly={readOnly}
          value={value}
          onChange={debouncedResults}
          onClear={handleClear}
          onBlur={handleBlur}
          onFocus={handleFocus}
          options={options}
          highlightedIndex={highlightedIndex}
          setHighlightedIndex={setHighlightedIndex}
          onSelection={onSelectOption}
          setIsOpen={setIsOpen}
          isOpen={isOpen && !isLoading}
          showProperty={showProperty}
          keyProperty={keyProperty}
          expand={expand}
          testId={testId}
          highlightedSubString={input.value ? '' : value}
          showChevron={false}
          pointer={false}
          overAll={overAll}
        />
      </InputContainer>
    </ThemeProvider>
  );
};

export default AsyncAutoComplete;
